// ANANT BRANDMARK — Viral shop-branding tool
// 100% client-side canvas processing → zero backend cost for image ops.
// Subscription-gated (300 AT/month · 3000 AT/year) — Affiliate Network 33% distributed.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, Wallet, ImagePlus, Download, Copy, MapPin, RefreshCw, Lock, Zap, Palette, Loader2, Globe } from 'lucide-react';
import QRCode from 'qrcode';
import api from '../../utils/api';
import ATWalletBadge from '../../components/ATWalletBadge';

const BASE_W = 1080;
const BASE_H = 1080;

// 32+ Ready-made frame presets — theme + border-style + background pattern combos
// style: metallic|double|dashed|rounded|stars|hearts|ribbon|zigzag|dots|chevron
// bg:    solid|diag|radial|polka|confetti|rays|stripes
const FRAME_PRESETS = [
  // ROYAL & METALLIC
  { id: 'gold',       label: '👑 Royal Gold',      primary: '#f2c744', secondary: '#a8791d', accent: '#fff3c4', style: 'metallic', bg: 'diag',    bgA: '#0b0b0b', bgB: '#2a1e04', emoji: '✦', cat: 'royal' },
  { id: 'rose',       label: '🌹 Rose Gold',       primary: '#e8b5c4', secondary: '#c47a8f', accent: '#fce8ee', style: 'metallic', bg: 'radial',  bgA: '#2b1620', bgB: '#0d0709', emoji: '❦', cat: 'royal' },
  { id: 'platinum',   label: '⚡ Platinum',        primary: '#c9d4de', secondary: '#6b7a89', accent: '#eaf1f8', style: 'metallic', bg: 'diag',    bgA: '#0d1218', bgB: '#232f3e', emoji: '◆', cat: 'royal' },
  { id: 'onyx',       label: '⚫ Onyx Gold',       primary: '#f2c744', secondary: '#1a1a1a', accent: '#f4d97f', style: 'double',   bg: 'solid',   bgA: '#000000', bgB: '#000000', emoji: '✧', cat: 'royal' },
  { id: 'sapphire',   label: '💎 Sapphire',        primary: '#4d7dff', secondary: '#0b1f5c', accent: '#c4d5ff', style: 'metallic', bg: 'radial',  bgA: '#050b1f', bgB: '#0d1a45', emoji: '◈', cat: 'royal' },
  { id: 'emerald',    label: '🟢 Emerald Royal',   primary: '#3fbf7f', secondary: '#0f5233', accent: '#c5f2d9', style: 'metallic', bg: 'diag',    bgA: '#062219', bgB: '#0d3a24', emoji: '❋', cat: 'royal' },

  // FESTIVAL / DIWALI / TRADITIONAL
  { id: 'diwali',     label: '🪔 Diwali Glow',     primary: '#ffb43d', secondary: '#8b2500', accent: '#ffe08a', style: 'stars',    bg: 'rays',    bgA: '#1a0500', bgB: '#3d1200', emoji: '🪔', cat: 'festival' },
  { id: 'holi',       label: '🎨 Holi Splash',     primary: '#ff4d94', secondary: '#7a1854', accent: '#ffd44d', style: 'dots',     bg: 'confetti', bgA: '#1f0715', bgB: '#3d1136', emoji: '🎨', cat: 'festival' },
  { id: 'rakhi',      label: '🎗️ Rakhi Bond',     primary: '#e63946', secondary: '#5a0d17', accent: '#ffd166', style: 'ribbon',   bg: 'diag',    bgA: '#1a0207', bgB: '#3a0913', emoji: '🎗️', cat: 'festival' },
  { id: 'eid',        label: '🌙 Eid Moon',        primary: '#5cd6a0', secondary: '#0b3f2b', accent: '#e8f7ee', style: 'stars',    bg: 'radial',  bgA: '#04170f', bgB: '#0a3b26', emoji: '🌙', cat: 'festival' },
  { id: 'christmas',  label: '🎄 Christmas',       primary: '#d62828', secondary: '#014f18', accent: '#fefae0', style: 'double',   bg: 'polka',   bgA: '#0a1a10', bgB: '#1a2e1f', emoji: '❄', cat: 'festival' },

  // WEDDING & LOVE
  { id: 'wedding',    label: '💒 Wedding Bliss',   primary: '#c9184a', secondary: '#3d0817', accent: '#ffb4b4', style: 'hearts',   bg: 'radial',  bgA: '#1a0409', bgB: '#3a0b1a', emoji: '💕', cat: 'wedding' },
  { id: 'anniv',      label: '💍 Anniversary',     primary: '#ffd700', secondary: '#8b0000', accent: '#fff4c4', style: 'hearts',   bg: 'diag',    bgA: '#1a0000', bgB: '#3a0808', emoji: '💗', cat: 'wedding' },
  { id: 'engage',     label: '💐 Engagement',      primary: '#ff85a1', secondary: '#701735', accent: '#fce8ee', style: 'ribbon',   bg: 'confetti', bgA: '#1f0813', bgB: '#3d132b', emoji: '💐', cat: 'wedding' },
  { id: 'blessed',    label: '🙏 Blessed',         primary: '#fabf3d', secondary: '#5c3b06', accent: '#fff2cf', style: 'metallic', bg: 'rays',    bgA: '#1a1004', bgB: '#3a2408', emoji: '🕉️', cat: 'wedding' },

  // BIRTHDAY & CELEBRATION
  { id: 'bday',       label: '🎂 Birthday Fun',    primary: '#ff6b9d', secondary: '#6a1052', accent: '#ffe66d', style: 'stars',    bg: 'confetti', bgA: '#150618', bgB: '#2d0a36', emoji: '🎂', cat: 'party' },
  { id: 'party',      label: '🎉 Party Time',      primary: '#f4a261', secondary: '#5a2e00', accent: '#e9c46a', style: 'dots',     bg: 'confetti', bgA: '#1a0d00', bgB: '#3a1c00', emoji: '🎉', cat: 'party' },
  { id: 'cheers',     label: '🥂 Cheers',          primary: '#e0aaff', secondary: '#5a189a', accent: '#f5deff', style: 'stars',    bg: 'radial',  bgA: '#0f0518', bgB: '#28093f', emoji: '✨', cat: 'party' },
  { id: 'kids',       label: '🧸 Kids Joy',        primary: '#48cae4', secondary: '#023e8a', accent: '#caf0f8', style: 'zigzag',   bg: 'polka',   bgA: '#031f3f', bgB: '#052d5c', emoji: '🎈', cat: 'party' },

  // SHOP / BUSINESS / OFFER
  { id: 'sale',       label: '🔥 Mega Sale',       primary: '#ff073a', secondary: '#4d0212', accent: '#ffe066', style: 'chevron',  bg: 'stripes', bgA: '#150106', bgB: '#3d030f', emoji: '🔥', cat: 'shop' },
  { id: 'newlaunch',  label: '🚀 New Launch',      primary: '#00f5ff', secondary: '#003844', accent: '#caf0f8', style: 'zigzag',   bg: 'rays',    bgA: '#001820', bgB: '#003844', emoji: '🚀', cat: 'shop' },
  { id: 'premium',    label: '⭐ Premium Store',   primary: '#ffd60a', secondary: '#001d3d', accent: '#ffefb0', style: 'double',   bg: 'diag',    bgA: '#000814', bgB: '#001d3d', emoji: '⭐', cat: 'shop' },
  { id: 'organic',    label: '🌿 Organic',         primary: '#95d5b2', secondary: '#1b4332', accent: '#d8f3dc', style: 'rounded',  bg: 'radial',  bgA: '#081a12', bgB: '#132a1d', emoji: '🌿', cat: 'shop' },
  { id: 'coffee',     label: '☕ Cafe',            primary: '#d4a373', secondary: '#5a3a1c', accent: '#faedcd', style: 'rounded',  bg: 'solid',   bgA: '#2b1a0d', bgB: '#2b1a0d', emoji: '☕', cat: 'shop' },
  { id: 'foodie',     label: '🍽️ Foodie',         primary: '#ff9770', secondary: '#8b2500', accent: '#ffd670', style: 'chevron',  bg: 'stripes', bgA: '#1a0700', bgB: '#3a1200', emoji: '🍽️', cat: 'shop' },

  // MODERN / MINIMAL
  { id: 'mono',       label: '⚪ Monochrome',      primary: '#ffffff', secondary: '#666666', accent: '#e5e5e5', style: 'rounded',  bg: 'solid',   bgA: '#0a0a0a', bgB: '#0a0a0a', emoji: '', cat: 'modern' },
  { id: 'minimal',    label: '✏️ Clean Minimal',   primary: '#212529', secondary: '#6c757d', accent: '#f8f9fa', style: 'rounded',  bg: 'solid',   bgA: '#f8f9fa', bgB: '#f8f9fa', textDark: true, emoji: '', cat: 'modern' },
  { id: 'neon',       label: '💜 Neon Glow',       primary: '#c77dff', secondary: '#3c096c', accent: '#f5c7ff', style: 'double',   bg: 'radial',  bgA: '#0a0014', bgB: '#1e0530', emoji: '⚡', cat: 'modern' },
  { id: 'sunset',     label: '🌅 Sunset',          primary: '#ffb703', secondary: '#fb5607', accent: '#ffd166', style: 'metallic', bg: 'diag',    bgA: '#3a0b00', bgB: '#8b3a00', emoji: '🌅', cat: 'modern' },

  // FUN / EMOJI
  { id: 'love',       label: '❤️ Love',            primary: '#ff006e', secondary: '#590019', accent: '#ffb3c8', style: 'hearts',   bg: 'polka',   bgA: '#1a0009', bgB: '#3a0018', emoji: '❤️', cat: 'fun' },
  { id: 'star',       label: '🌟 Superstar',       primary: '#ffd700', secondary: '#4a3800', accent: '#fff2b3', style: 'stars',    bg: 'rays',    bgA: '#0a0800', bgB: '#2e2200', emoji: '⭐', cat: 'fun' },
  { id: 'tropical',   label: '🌴 Tropical',        primary: '#06d6a0', secondary: '#003049', accent: '#ffd166', style: 'zigzag',   bg: 'diag',    bgA: '#00202e', bgB: '#004659', emoji: '🌴', cat: 'fun' },
];

const FRAME_CATEGORIES = [
  { id: 'all',      label: '✨ All', color: 'from-fuchsia-500 to-pink-500' },
  { id: 'royal',    label: '👑 Royal', color: 'from-amber-500 to-yellow-400' },
  { id: 'festival', label: '🪔 Festival', color: 'from-orange-500 to-red-500' },
  { id: 'wedding',  label: '💒 Wedding', color: 'from-rose-500 to-pink-400' },
  { id: 'party',    label: '🎉 Party', color: 'from-cyan-400 to-blue-400' },
  { id: 'shop',     label: '🔥 Shop', color: 'from-red-500 to-orange-400' },
  { id: 'modern',   label: '⚪ Modern', color: 'from-slate-400 to-white' },
  { id: 'fun',      label: '🌟 Fun', color: 'from-green-400 to-emerald-500' },
];

// ==== Canvas helpers ====
function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
function drawStar(ctx, cx, cy, r, fill) {
  ctx.save(); ctx.fillStyle = fill; ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const ang = (Math.PI / 5) * i - Math.PI / 2;
    const rad = i % 2 === 0 ? r : r / 2.4;
    const x = cx + Math.cos(ang) * rad, y = cy + Math.sin(ang) * rad;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath(); ctx.fill(); ctx.restore();
}
function drawHeart(ctx, cx, cy, s, fill) {
  ctx.save(); ctx.fillStyle = fill; ctx.beginPath();
  ctx.moveTo(cx, cy + s / 3);
  ctx.bezierCurveTo(cx, cy, cx - s, cy, cx - s, cy + s / 3);
  ctx.bezierCurveTo(cx - s, cy + (2 * s) / 3, cx, cy + (2 * s) / 3, cx, cy + s);
  ctx.bezierCurveTo(cx, cy + (2 * s) / 3, cx + s, cy + (2 * s) / 3, cx + s, cy + s / 3);
  ctx.bezierCurveTo(cx + s, cy, cx, cy, cx, cy + s / 3);
  ctx.closePath(); ctx.fill(); ctx.restore();
}

export default function BrandMark() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  const [photoFile, setPhotoFile] = useState(null);
  const [photoImg, setPhotoImg] = useState(null);
  const [logoImg, setLogoImg] = useState(null);
  const [shopName, setShopName] = useState('');
  const [shopTagline, setShopTagline] = useState('');
  const [qrUrl, setQrUrl] = useState('');           // shop's Google Maps or Anant promo link
  const [frame, setFrame] = useState(FRAME_PRESETS[0]);
  const [frameCat, setFrameCat] = useState('all');
  const filteredFrames = useMemo(() => frameCat === 'all' ? FRAME_PRESETS : FRAME_PRESETS.filter((f) => f.cat === frameCat), [frameCat]);
  const [captionIdx, setCaptionIdx] = useState(0);
  const [captionLang, setCaptionLang] = useState(() => localStorage.getItem('brandmark:lang') || 'en');
  const [customFeedback, setCustomFeedback] = useState(() => localStorage.getItem('brandmark:customFeedback') || '');
  const [useCustom, setUseCustom] = useState(() => localStorage.getItem('brandmark:useCustom') === '1');
  const [rendering, setRendering] = useState(false);
  // Extra branding fields (up to 3 URLs + up to 3 mobile numbers)
  const [extras, setExtras] = useState(() => {
    try { return JSON.parse(localStorage.getItem('brandmark:extras')) || { url1: '', url2: '', url3: '', mob1: '', mob2: '', mob3: '' }; }
    catch { return { url1: '', url2: '', url3: '', mob1: '', mob2: '', mob3: '' }; }
  });
  const updExt = (k, v) => setExtras((prev) => { const n = { ...prev, [k]: v }; localStorage.setItem('brandmark:extras', JSON.stringify(n)); return n; });
  // Stickers (emoji overlays user can add to canvas)
  const STICKER_OPTIONS = ['✨', '🎉', '🔥', '❤️', '💎', '⭐', '🎁', '🌟', '💯', '🙏', '🏆', '👑', '🎊', '🌸', '💫'];
  const [stickers, setStickers] = useState([]);   // [{emoji, x, y}]
  // Live countdown ticker (DD:HH:MM:SS)
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 1000); return () => clearInterval(t); }, []);
  const countdown = useMemo(() => {
    const exp = config?.subscription?.expires_at;
    if (!exp) return null;
    const ms = new Date(exp).getTime() - nowTick;
    if (ms <= 0) return { expired: true, dd: 0, hh: 0, mm: 0, ss: 0 };
    const s = Math.floor(ms / 1000);
    return { expired: false, dd: Math.floor(s / 86400), hh: Math.floor((s % 86400) / 3600), mm: Math.floor((s % 3600) / 60), ss: s % 60 };
  }, [config, nowTick]);
  const fmt2 = (n) => String(n).padStart(2, '0');

  // ===== Blinking Text (multi-font + attractive styles) =====
  const BLINK_FONTS = [
    { id: 'impact',   label: 'Impact · Bold',        css: 'Impact, "Arial Black", sans-serif' },
    { id: 'serif',    label: 'Elegant Serif',        css: 'Georgia, "Times New Roman", serif' },
    { id: 'mono',     label: 'Mono / Retro',         css: '"Courier New", monospace' },
    { id: 'cursive',  label: 'Cursive / Script',     css: '"Brush Script MT", cursive' },
    { id: 'display',  label: 'Display · Wide',       css: '"Trebuchet MS", "Arial Black", sans-serif' },
    { id: 'condensed',label: 'Condensed',            css: '"Arial Narrow", "Helvetica", sans-serif' },
    { id: 'fancy',    label: 'Fancy / Fantasy',      css: '"Papyrus", "Copperplate", fantasy' },
    { id: 'devnagari',label: 'Devanagari Bold',      css: '"Noto Sans Devanagari", "Mangal", sans-serif' },
  ];
  const BLINK_STYLES = [
    { id: 'neon',    label: '💡 Neon Glow',   color: '#00f5ff', shadow: '#ff00e6' },
    { id: 'gold',    label: '🏆 Gold Fire',   color: '#ffd60a', shadow: '#ff4500' },
    { id: 'rainbow', label: '🌈 Rainbow',     color: 'rainbow', shadow: '#ffffff' },
    { id: 'red',     label: '🔥 Red Alert',   color: '#ff073a', shadow: '#ffff00' },
    { id: 'white',   label: '⚡ White Flash', color: '#ffffff', shadow: '#000000' },
    { id: 'green',   label: '💚 Emerald',     color: '#39ff14', shadow: '#004d00' },
  ];
  const [blinkText, setBlinkText] = useState(() => localStorage.getItem('brandmark:blinkText') || '');
  const [blinkFont, setBlinkFont] = useState(() => localStorage.getItem('brandmark:blinkFont') || 'impact');
  const [blinkStyle, setBlinkStyle] = useState(() => localStorage.getItem('brandmark:blinkStyle') || 'neon');
  useEffect(() => { localStorage.setItem('brandmark:blinkText', blinkText); }, [blinkText]);
  useEffect(() => { localStorage.setItem('brandmark:blinkFont', blinkFont); }, [blinkFont]);
  useEffect(() => { localStorage.setItem('brandmark:blinkStyle', blinkStyle); }, [blinkStyle]);
  // Animation phase — 0..1..0..1 ~2Hz for blink effect
  const [blinkPhase, setBlinkPhase] = useState(0);
  useEffect(() => {
    if (!blinkText) return;
    const t = setInterval(() => setBlinkPhase((p) => (p + 1) % 6), 250);
    return () => clearInterval(t);
  }, [blinkText]);
  // Recording state for animated WebM export
  const [recording, setRecording] = useState(false);
  const recordRef = useRef(null);

  const activeSub = config?.subscription?.active && !(countdown && countdown.expired);
  const captionsMulti = useMemo(() => config?.viral_captions_multi || { en: config?.viral_captions || [] }, [config]);
  const captions = useMemo(() => captionsMulti[captionLang] || captionsMulti.en || [], [captionsMulti, captionLang]);
  const languageLabels = useMemo(() => config?.language_labels || { en: 'English' }, [config]);

  // Load config on mount + rotate captions every 6s
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/brandmark/config');
        setConfig(r.data);
        // Prefill from localStorage
        setShopName(localStorage.getItem('brandmark:shopName') || '');
        setShopTagline(localStorage.getItem('brandmark:shopTagline') || '');
        setQrUrl(localStorage.getItem('brandmark:qrUrl') || '');
      } catch (e) {
        if (e?.response?.status === 401) navigate('/login');
        else toast.error('Config load failed');
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  useEffect(() => {
    if (!captions.length) return;
    const t = setInterval(() => setCaptionIdx((i) => (i + 1) % captions.length), 6000);
    return () => clearInterval(t);
  }, [captions.length]);

  const persist = (k, v) => { try { localStorage.setItem(`brandmark:${k}`, v); } catch (_) { /* noop */ } };

  // Load image helper
  const loadImg = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('Photo max 8 MB'); return; }
    setPhotoFile(f);
    try { setPhotoImg(await loadImg(f)); } catch { toast.error('Photo load failed'); }
  };

  const onPickLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('Logo max 2 MB'); return; }
    try { setLogoImg(await loadImg(f)); } catch { toast.error('Logo load failed'); }
  };

  // Draw the branded composition (100% client-side canvas)
  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !photoImg) return;
    setRendering(true);
    try {
      canvas.width = BASE_W;
      canvas.height = BASE_H;
      const ctx = canvas.getContext('2d');

      // ===== BACKGROUND (pattern based on frame.bg) =====
      const drawBg = () => {
        ctx.fillStyle = frame.bgA || '#0b0b0b';
        ctx.fillRect(0, 0, BASE_W, BASE_H);
        if (frame.bg === 'diag') {
          const g = ctx.createLinearGradient(0, 0, BASE_W, BASE_H);
          g.addColorStop(0, frame.bgA); g.addColorStop(1, frame.bgB || frame.bgA);
          ctx.fillStyle = g; ctx.fillRect(0, 0, BASE_W, BASE_H);
        } else if (frame.bg === 'radial') {
          const g = ctx.createRadialGradient(BASE_W / 2, BASE_H / 2, 0, BASE_W / 2, BASE_H / 2, BASE_W * 0.7);
          g.addColorStop(0, frame.bgB || frame.bgA); g.addColorStop(1, frame.bgA);
          ctx.fillStyle = g; ctx.fillRect(0, 0, BASE_W, BASE_H);
        } else if (frame.bg === 'polka') {
          ctx.fillStyle = frame.accent + '20';
          for (let x = 30; x < BASE_W; x += 60) for (let y = 30; y < BASE_H; y += 60) {
            ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.fill();
          }
        } else if (frame.bg === 'stripes') {
          ctx.fillStyle = frame.bgB || frame.bgA;
          for (let x = -BASE_H; x < BASE_W; x += 60) {
            ctx.save(); ctx.translate(x, 0); ctx.rotate(Math.PI / 4);
            ctx.fillRect(0, 0, 20, BASE_H * 2); ctx.restore();
          }
        } else if (frame.bg === 'confetti') {
          const cols = [frame.primary, frame.accent, frame.secondary];
          for (let i = 0; i < 90; i++) {
            ctx.fillStyle = cols[i % 3] + '55';
            const x = Math.random() * BASE_W, y = Math.random() * BASE_H, s = 4 + Math.random() * 8;
            ctx.fillRect(x, y, s, s);
          }
        } else if (frame.bg === 'rays') {
          ctx.save(); ctx.translate(BASE_W / 2, BASE_H / 2);
          for (let i = 0; i < 24; i++) {
            ctx.rotate(Math.PI / 12);
            const g = ctx.createLinearGradient(0, 0, 0, -BASE_W);
            g.addColorStop(0, frame.primary + '30'); g.addColorStop(1, 'transparent');
            ctx.fillStyle = g; ctx.fillRect(-20, 0, 40, -BASE_W);
          }
          ctx.restore();
        }
      };
      drawBg();

      // ===== PHOTO (rounded corners for modern styles) =====
      const pad = 44;
      const photoW = BASE_W - pad * 2;
      const photoH = BASE_H - pad * 2 - 200;
      const scale = Math.max(photoW / photoImg.width, photoH / photoImg.height);
      const dw = photoImg.width * scale;
      const dh = photoImg.height * scale;
      const dx = pad + (photoW - dw) / 2;
      const dy = pad + (photoH - dh) / 2;
      ctx.save();
      ctx.beginPath();
      const cornerR = frame.style === 'rounded' ? 40 : 0;
      if (cornerR > 0) roundRectPath(ctx, pad, pad, photoW, photoH, cornerR);
      else ctx.rect(pad, pad, photoW, photoH);
      ctx.clip();
      ctx.drawImage(photoImg, dx, dy, dw, dh);
      ctx.restore();

      // ===== OUTER BORDER (varies by frame.style) =====
      const grad = ctx.createLinearGradient(0, 0, BASE_W, BASE_H);
      grad.addColorStop(0, frame.primary);
      grad.addColorStop(0.5, frame.accent);
      grad.addColorStop(1, frame.secondary);
      ctx.strokeStyle = grad;
      ctx.lineWidth = 22;
      if (frame.style === 'rounded') {
        roundRectPath(ctx, 11, 11, BASE_W - 22, BASE_H - 22, 60);
        ctx.stroke();
      } else if (frame.style === 'dashed') {
        ctx.setLineDash([28, 14]); ctx.strokeRect(11, 11, BASE_W - 22, BASE_H - 22); ctx.setLineDash([]);
      } else if (frame.style === 'dots') {
        ctx.setLineDash([4, 20]); ctx.lineWidth = 18; ctx.strokeRect(11, 11, BASE_W - 22, BASE_H - 22); ctx.setLineDash([]); ctx.lineWidth = 22;
      } else if (frame.style === 'double') {
        ctx.strokeRect(11, 11, BASE_W - 22, BASE_H - 22);
        ctx.lineWidth = 4; ctx.strokeStyle = frame.accent;
        ctx.strokeRect(30, 30, BASE_W - 60, BASE_H - 60);
      } else {
        ctx.strokeRect(11, 11, BASE_W - 22, BASE_H - 22);
      }
      // Inner thin frame around photo
      ctx.strokeStyle = frame.secondary;
      ctx.lineWidth = 2;
      if (cornerR > 0) { roundRectPath(ctx, pad - 6, pad - 6, photoW + 12, photoH + 12, cornerR + 4); ctx.stroke(); }
      else ctx.strokeRect(pad - 6, pad - 6, photoW + 12, photoH + 12);

      // ===== CORNER / STYLE DECORATIONS =====
      const drawStyleDecor = () => {
        const positions = [[70, 70], [BASE_W - 70, 70], [70, pad + photoH - 14], [BASE_W - 70, pad + photoH - 14]];
        if (frame.style === 'stars') {
          positions.forEach(([x, y]) => drawStar(ctx, x, y, 26, frame.primary));
        } else if (frame.style === 'hearts') {
          positions.forEach(([x, y]) => drawHeart(ctx, x, y, 22, frame.primary));
        } else if (frame.style === 'ribbon') {
          ctx.fillStyle = frame.primary;
          ctx.fillRect(BASE_W / 2 - 90, 4, 180, 40);
          ctx.beginPath(); ctx.moveTo(BASE_W / 2 - 90, 4); ctx.lineTo(BASE_W / 2 - 110, 44); ctx.lineTo(BASE_W / 2 - 90, 44); ctx.fill();
          ctx.beginPath(); ctx.moveTo(BASE_W / 2 + 90, 4); ctx.lineTo(BASE_W / 2 + 110, 44); ctx.lineTo(BASE_W / 2 + 90, 44); ctx.fill();
        } else if (frame.style === 'zigzag') {
          ctx.strokeStyle = frame.primary; ctx.lineWidth = 5;
          [[35, 35, BASE_W - 35, 35], [35, BASE_H - 240, BASE_W - 35, BASE_H - 240]].forEach(([x1, y1, x2, y2]) => {
            ctx.beginPath();
            for (let x = x1; x <= x2; x += 30) { ctx.lineTo(x, y1 + ((x / 15) % 2 === 0 ? 0 : 10)); }
            ctx.stroke();
          });
        } else if (frame.style === 'chevron') {
          ctx.fillStyle = frame.primary;
          for (let x = 40; x < BASE_W - 40; x += 60) {
            ctx.beginPath(); ctx.moveTo(x, 30); ctx.lineTo(x + 30, 15); ctx.lineTo(x + 60, 30); ctx.lineTo(x + 30, 45); ctx.closePath(); ctx.fill();
          }
        } else if (frame.style === 'metallic') {
          // Classic diagonal gold corners (original)
          const cornerSize = 34;
          [[pad - 6, pad - 6], [BASE_W - pad - cornerSize + 6, pad - 6], [pad - 6, pad + photoH + 6 - cornerSize], [BASE_W - pad - cornerSize + 6, pad + photoH + 6 - cornerSize]].forEach(([cx, cy]) => {
            ctx.fillStyle = frame.primary;
            ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + cornerSize, cy); ctx.lineTo(cx, cy + cornerSize); ctx.closePath(); ctx.fill();
          });
        }
      };
      drawStyleDecor();
      // Bottom branding bar
      const barY = pad + photoH + 20;
      const barH = BASE_H - barY - pad;
      const barGrad = ctx.createLinearGradient(0, barY, 0, barY + barH);
      barGrad.addColorStop(0, '#0f0f0f');
      barGrad.addColorStop(1, '#1a1a1a');
      ctx.fillStyle = barGrad;
      ctx.fillRect(pad, barY, photoW, barH);
      ctx.strokeStyle = frame.primary;
      ctx.lineWidth = 3;
      ctx.strokeRect(pad, barY, photoW, barH);
      // Logo (left of bar, if present)
      const logoSize = 130;
      const logoX = pad + 30;
      const logoY = barY + (barH - logoSize) / 2;
      if (logoImg) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.strokeStyle = frame.primary;
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.clip();
        ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
        ctx.restore();
      } else {
        // Placeholder circle with shop initial
        ctx.beginPath();
        ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
        ctx.strokeStyle = frame.primary;
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.fillStyle = frame.accent;
        ctx.fill();
        ctx.fillStyle = frame.secondary;
        ctx.font = 'bold 80px Georgia';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((shopName || 'A')[0].toUpperCase(), logoX + logoSize / 2, logoY + logoSize / 2);
      }
      // Shop name + tagline (center-left)
      const textX = logoX + logoSize + 30;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      // Drop shadow
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 2;
      ctx.fillStyle = frame.primary;
      ctx.font = 'bold 54px Georgia, serif';
      ctx.fillText((shopName || 'YOUR SHOP NAME').toUpperCase(), textX, barY + barH / 2 - 22);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      ctx.fillStyle = frame.accent;
      ctx.font = 'italic 28px Georgia, serif';
      ctx.fillText(shopTagline || 'Premium Quality · Trusted Service', textX, barY + barH / 2 + 30);
      // QR code (right side of bar)
      const qrSize = 140;
      const qrX = pad + photoW - qrSize - 20;
      const qrY = barY + (barH - qrSize) / 2;
      if (qrUrl && qrUrl.trim()) {
        try {
          const qrDataUrl = await QRCode.toDataURL(qrUrl.trim(), { width: qrSize * 2, margin: 1, color: { dark: '#000', light: '#fff' } });
          const qrImg = new Image();
          await new Promise((res, rej) => { qrImg.onload = res; qrImg.onerror = rej; qrImg.src = qrDataUrl; });
          // White backing for QR
          ctx.fillStyle = '#fff';
          ctx.fillRect(qrX - 6, qrY - 6, qrSize + 12, qrSize + 12);
          ctx.strokeStyle = frame.primary;
          ctx.lineWidth = 3;
          ctx.strokeRect(qrX - 6, qrY - 6, qrSize + 12, qrSize + 12);
          ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
          // "SCAN" label above QR
          ctx.fillStyle = frame.primary;
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('SCAN', qrX + qrSize / 2, qrY - 14);
        } catch (_) { /* qr failed → skip */ }
      }
      // ===== EXTRA CONTACT STRIP (bottom of frame — up to 3 URLs / 3 mobiles) =====
      const extraLines = [];
      [extras?.url1, extras?.url2, extras?.url3].forEach((u) => { if (u && u.trim()) extraLines.push('🔗 ' + u.trim()); });
      [extras?.mob1, extras?.mob2, extras?.mob3].forEach((m) => { if (m && m.trim()) extraLines.push('📞 ' + m.trim()); });
      if (extraLines.length > 0) {
        const stripY = BASE_H - 40;
        // Ribbon background
        const rg = ctx.createLinearGradient(0, stripY - 6, 0, BASE_H);
        rg.addColorStop(0, frame.primary); rg.addColorStop(1, frame.secondary);
        ctx.fillStyle = rg;
        ctx.fillRect(0, stripY - 6, BASE_W, 46);
        ctx.fillStyle = frame.textDark ? '#111' : '#fff';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(extraLines.slice(0, 3).join('   ·   '), BASE_W / 2, stripY + 16);
        if (extraLines.length > 3) {
          ctx.font = '14px Arial';
          ctx.fillText(extraLines.slice(3).join('   ·   '), BASE_W / 2, stripY + 34);
        }
      }
      // ===== BLINKING TEXT (multi-font · attractive · animated during recording) =====
      if (blinkText && blinkText.trim()) {
        const bfont = BLINK_FONTS.find((f) => f.id === blinkFont) || BLINK_FONTS[0];
        const bstyle = BLINK_STYLES.find((s) => s.id === blinkStyle) || BLINK_STYLES[0];
        const isBright = blinkPhase < 3;   // half the time bright, half dim
        const alpha = isBright ? 1.0 : 0.35;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        // Auto-fit: 46px base, shrink if too long
        let fontSize = 60;
        ctx.font = `bold ${fontSize}px ${bfont.css}`;
        while (ctx.measureText(blinkText).width > BASE_W - 100 && fontSize > 28) {
          fontSize -= 4;
          ctx.font = `bold ${fontSize}px ${bfont.css}`;
        }
        const textX = BASE_W / 2;
        const textY = pad + photoH - 70;   // near bottom of photo
        // Shadow / glow
        ctx.shadowColor = bstyle.shadow;
        ctx.shadowBlur = isBright ? 30 : 12;
        ctx.lineWidth = 8;
        ctx.strokeStyle = '#000';
        ctx.strokeText(blinkText, textX, textY);
        // Fill (rainbow uses gradient)
        if (bstyle.color === 'rainbow') {
          const rg = ctx.createLinearGradient(textX - 300, textY, textX + 300, textY);
          const shift = blinkPhase * 60;
          rg.addColorStop(0, `hsl(${(0 + shift) % 360}, 100%, 60%)`);
          rg.addColorStop(0.5, `hsl(${(120 + shift) % 360}, 100%, 60%)`);
          rg.addColorStop(1, `hsl(${(240 + shift) % 360}, 100%, 60%)`);
          ctx.fillStyle = rg;
        } else {
          ctx.fillStyle = bstyle.color;
        }
        ctx.fillText(blinkText, textX, textY);
        ctx.restore();
      }

      // ===== EMOJI STICKERS =====
      if (stickers.length > 0) {
        ctx.font = '68px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        stickers.forEach((s) => {
          ctx.shadowColor = 'rgba(0,0,0,0.4)';
          ctx.shadowBlur = 10;
          ctx.fillText(s.emoji, s.x, s.y);
        });
        ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0;
      }
      // Top-right "Powered by" badge
      ctx.textAlign = 'right';
      ctx.fillStyle = frame.primary;
      ctx.font = 'bold 20px Arial';
      ctx.fillText('Anant BrandMark', BASE_W - pad - 6, pad + 24);
      ctx.fillStyle = frame.accent;
      ctx.font = '14px Arial';
      ctx.fillText('anantwebportal.com', BASE_W - pad - 6, pad + 44);
    } finally {
      setRendering(false);
    }
  }, [photoImg, logoImg, shopName, shopTagline, qrUrl, frame, extras, stickers, blinkText, blinkFont, blinkStyle, blinkPhase]);

  // Re-render on any input change
  useEffect(() => { draw(); }, [draw]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas || !photoImg) { toast.error('Pehle photo select karo'); return; }
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BrandMark-${(shopName || 'shop').replace(/\s+/g, '-')}-${Date.now()}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded! Share on social media 🚀');
    }, 'image/jpeg', 0.92);
  };

  const copyCaption = () => {
    navigator.clipboard.writeText(buildShareText()).then(() => toast.success('Caption + contacts copied!'));
  };

  // Animated WebM video export (real blinking — canvas.captureStream + MediaRecorder)
  const recordVideo = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !photoImg) { toast.error('Load photo first'); return; }
    if (!canvas.captureStream || typeof MediaRecorder === 'undefined') { toast.error('Browser does not support video export'); return; }
    setRecording(true);
    try {
      const stream = canvas.captureStream(15);
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2_500_000 });
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `brandmark_${Date.now()}.webm`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        toast.success('🎬 Video saved! Share it on WhatsApp status');
        setRecording(false);
      };
      rec.start();
      // Record 3 seconds — draw loop is already running from blinkPhase ticker
      setTimeout(() => { try { rec.stop(); } catch (_) { setRecording(false); } }, 3000);
    } catch (e) { toast.error('Recording failed: ' + (e.message || 'unknown')); setRecording(false); }
  };

  const buildShareText = () => {
    const cap = (useCustom && customFeedback.trim()) ? customFeedback.trim() : (captions[captionIdx] || '');
    const extraBits = [];
    if (blinkText && blinkText.trim()) extraBits.push('✨ *' + blinkText.trim() + '* ✨');
    [extras?.url1, extras?.url2, extras?.url3].forEach((u) => { if (u && u.trim()) extraBits.push('🔗 ' + u.trim()); });
    [extras?.mob1, extras?.mob2, extras?.mob3].forEach((m) => { if (m && m.trim()) extraBits.push('📞 ' + m.trim()); });
    return extraBits.length ? `${cap}\n\n${extraBits.join('\n')}` : cap;
  };

  const shareWhatsApp = async () => {
    const canvas = canvasRef.current;
    const cap = buildShareText();
    if (!canvas || !photoImg) {
      // Fallback: no image yet → just share text
      window.open(`https://wa.me/?text=${encodeURIComponent(cap)}`, '_blank');
      return;
    }
    // Try Web Share API Level 2 (mobile Chrome / Safari) — shares IMAGE + TEXT together
    try {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      if (!blob) throw new Error('Canvas blob failed');
      const file = new File([blob], `BrandMark-${(shopName || 'shop').replace(/\s+/g, '-')}.jpg`, { type: 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: shopName || 'Anant BrandMark',
          text: cap,
        });
        toast.success('Shared! Photo + caption both included 🚀');
        return;
      }
    } catch (err) {
      if (err.name === 'AbortError') return;   // user cancelled share sheet
      // Continue to fallback below
    }
    // Desktop fallback — download image then open wa.me with caption
    // (Web Share API not available OR file share not supported)
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `BrandMark-${(shopName || 'shop').replace(/\s+/g, '-')}-${Date.now()}.jpg`;
        a.click();
        URL.revokeObjectURL(url);
      }
    }, 'image/jpeg', 0.92);
    toast.info('Photo downloaded! Ab WhatsApp me photo attach karo aur caption paste karo 📎');
    setTimeout(() => window.open(`https://wa.me/?text=${encodeURIComponent(cap)}`, '_blank'), 800);
  };

  const buyPlan = async (planId) => {
    if (!window.confirm(`${PLANS_LABELS[planId]} activate karna hai?\n${planId === 'monthly' ? '300 AT' : '3000 AT'} deduct hoga aur 7-level Affiliate Network me distribute hoga.`)) return;
    setSubscribing(true);
    try {
      const r = await api.post('/brandmark/subscribe', { plan_id: planId });
      toast.success(r.data.message || 'Subscription activated 🎉');
      // Refresh config
      const cfg = await api.get('/brandmark/config');
      setConfig(cfg.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Subscribe failed');
    } finally { setSubscribing(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
    </div>
  );

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white">
      {/* ── Top bar (AT wallet + Buy AT — same pattern as other tools) ── */}
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-amber-400/50 px-4 py-2 flex items-center justify-between shadow-2xl shadow-black">
        <Link to="/" className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><span>← Home</span></Link>
        <ATWalletBadge compact />
      </div>

      {/* ── Hero ── */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500/20 to-rose-500/20 border border-amber-400/40 text-[10px] font-black tracking-widest text-amber-200 mb-3">
            <Sparkles className="w-3 h-3" /> ANANT AI TRIAL ROOM · NEW TOOL
          </div>
          <h1 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-300 via-rose-300 to-amber-300 bg-clip-text text-transparent">Anant BrandMark</h1>
          <p className="text-sm text-slate-300 mt-2 max-w-2xl mx-auto">Turn every customer photo into <b className="text-amber-300">viral shop advertising</b> — Gold/Rose Gold branding, auto-QR, zero cost. Works fully offline in your browser.</p>
        </div>

        {/* ── Subscription paywall ── */}
        {!activeSub ? (
          <div className="rounded-3xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 via-slate-900 to-rose-950/40 p-6 mb-6" data-testid="brandmark-paywall">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-5 h-5 text-amber-400" />
              <div className="text-lg font-black text-amber-200">Subscribe to Unlock BrandMark</div>
            </div>
            <div className="text-sm text-slate-300 mb-4">Sirf active subscribers ke liye. Har purchase pe <b className="text-amber-300">33% (7-level Affiliate Network)</b> aap ke upline ko milta hai instantly.</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(config?.plans || []).map((p) => (
                <button
                  key={p.id}
                  data-testid={`brandmark-plan-${p.id}`}
                  disabled={subscribing}
                  onClick={() => buyPlan(p.id)}
                  className={`text-left rounded-2xl border-2 p-4 transition-all hover:scale-[1.01] ${p.id === 'yearly' ? 'border-emerald-400/60 bg-gradient-to-br from-emerald-900/40 to-emerald-800/20' : 'border-amber-400/50 bg-gradient-to-br from-amber-900/40 to-amber-800/20'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest opacity-70">{p.label} Plan</div>
                    {p.id === 'yearly' && <div className="text-[9px] font-black px-2 py-0.5 rounded-full bg-emerald-500 text-black">BEST VALUE</div>}
                  </div>
                  <div className="text-3xl font-black mt-1">{p.amount} <span className="text-lg opacity-70">AT</span></div>
                  <div className="text-xs opacity-80 mt-1">{p.highlight}</div>
                  <div className="text-[10px] opacity-60 mt-2">Validity: {p.days} days</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-black text-amber-200"><Zap className="w-3 h-3" /> Tap to Subscribe</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className={`rounded-2xl border-2 p-3 mb-6 flex items-center justify-between flex-wrap gap-2 ${
            config?.subscription?.source === 'trial_room_combo' ? 'border-fuchsia-400/50 bg-fuchsia-500/10' :
            config?.subscription?.plan_id === 'free_trial' ? 'border-cyan-400/50 bg-cyan-500/10' :
            'border-emerald-400/40 bg-emerald-500/10'
          }`} data-testid="brandmark-active-badge">
            <div>
              <div className={`text-[9px] uppercase tracking-widest font-black ${
                config?.subscription?.source === 'trial_room_combo' ? 'text-fuchsia-300' :
                config?.subscription?.plan_id === 'free_trial' ? 'text-cyan-300' :
                'text-emerald-300'
              }`}>
                {config?.subscription?.source === 'trial_room_combo' ? '🎁 COMBO ACCESS · Via Trial Room' :
                 config?.subscription?.plan_id === 'free_trial' ? '🆓 FREE TRIAL · 2 DAYS' :
                 '✅ ACTIVE SUBSCRIPTION'}
              </div>
              <div className="text-sm">
                {config?.subscription?.plan_label || (config?.subscription?.plan_id || '').toUpperCase()}
                {countdown && !countdown.expired ? (
                  <> · <b data-testid="brandmark-countdown" className="font-mono text-amber-300">{fmt2(countdown.dd)}d : {fmt2(countdown.hh)}h : {fmt2(countdown.mm)}m : {fmt2(countdown.ss)}s left</b></>
                ) : countdown?.expired ? (
                  <> · <b className="text-red-400">EXPIRED — Upgrade now</b></>
                ) : (
                  <> · <b>{config?.subscription?.days_left ?? '—'} days left</b></>
                )}
              </div>
              {config?.subscription?.source === 'trial_room_combo' && (
                <div className="text-[10px] text-fuchsia-200/80 mt-0.5">Your Trial Room plan unlocks BrandMark automatically — enjoy!</div>
              )}
              {config?.subscription?.plan_id === 'free_trial' && (
                <div className="text-[10px] text-cyan-200/80 mt-0.5">Your welcome gift! Upgrade below to unlock lifetime + earn 33% Affiliate Network on referrals.</div>
              )}
            </div>
            {config?.subscription?.plan_id !== 'combo_trial_room' && (
              <button onClick={() => buyPlan(config?.subscription?.plan_id === 'monthly' ? 'monthly' : 'yearly')} disabled={subscribing} data-testid="brandmark-extend-btn" className="px-3 py-1.5 rounded-lg bg-amber-400 text-black text-xs font-black">
                {config?.subscription?.plan_id === 'free_trial' ? 'Upgrade' : 'Extend'}
              </button>
            )}
          </div>
        )}

        {/* ── Editor (only when active) ── */}
        {activeSub && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Inputs */}
            <div className="space-y-3">
              <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
                <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-3 flex items-center gap-1"><ImagePlus className="w-3 h-3" /> Step 1 · Photo & Logo</div>
                <label className="block mb-3">
                  <div className="text-xs text-slate-400 mb-1">Customer Photo (recommended: square, ≤8 MB)</div>
                  <input type="file" accept="image/*" onChange={onPickPhoto} data-testid="brandmark-photo-input" className="text-xs w-full" />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-400 mb-1">Shop Logo (optional, ≤2 MB)</div>
                  <input type="file" accept="image/*" onChange={onPickLogo} data-testid="brandmark-logo-input" className="text-xs w-full" />
                </label>
              </div>

              <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
                <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-3 flex items-center gap-1"><Palette className="w-3 h-3" /> Step 2 · Shop Details</div>
                <input value={shopName} onChange={(e) => { setShopName(e.target.value); persist('shopName', e.target.value); }} placeholder="Shop Name (e.g. Gogte Jewellers)" data-testid="brandmark-shopname" className="w-full mb-2 px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <input value={shopTagline} onChange={(e) => { setShopTagline(e.target.value); persist('shopTagline', e.target.value); }} placeholder="Tagline (e.g. Since 1975 · Trusted Jewellers)" data-testid="brandmark-tagline" className="w-full mb-2 px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <div className="text-xs text-slate-400 mb-1 mt-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> QR link (Google Maps / promo page)</div>
                <input value={qrUrl} onChange={(e) => { setQrUrl(e.target.value); persist('qrUrl', e.target.value); }} placeholder="https://maps.app.goo.gl/… OR https://anantwebportal.com/shop/xyz" data-testid="brandmark-qr" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
              </div>

              <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                  <div className="text-xs uppercase tracking-widest text-amber-300 font-black">Step 3 · Frame Style <span className="opacity-60 text-[9px]">({FRAME_PRESETS.length}+ ready-made)</span></div>
                  <div className="text-[10px] font-mono opacity-70">{filteredFrames.length} available</div>
                </div>
                {/* Category tabs */}
                <div className="flex flex-wrap gap-1 mb-3" data-testid="brandmark-frame-cats">
                  {FRAME_CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setFrameCat(c.id)}
                      data-testid={`brandmark-cat-${c.id}`}
                      className={`px-2 py-1 rounded-lg text-[10px] font-black transition-all border ${frameCat === c.id ? `bg-gradient-to-r ${c.color} text-black border-white/30 scale-105` : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
                {/* Frame grid */}
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-[320px] overflow-y-auto pr-1" data-testid="brandmark-frame-grid">
                  {filteredFrames.map((f) => (
                    <button
                      key={f.id}
                      data-testid={`brandmark-frame-${f.id}`}
                      onClick={() => setFrame(f)}
                      title={f.label}
                      className={`relative rounded-lg text-[10px] font-black border-2 transition-all p-2 min-h-[62px] flex flex-col items-center justify-center gap-0.5 ${frame.id === f.id ? 'scale-[1.05] ring-2 ring-white/40' : 'opacity-80 hover:opacity-100 hover:scale-[1.02]'}`}
                      style={{
                        borderColor: f.primary,
                        background: `linear-gradient(135deg, ${f.bgA || '#0b0b0b'}, ${f.bgB || f.bgA || '#0b0b0b'})`,
                        color: f.textDark ? '#111' : '#fff',
                      }}
                    >
                      {/* Mini corner accents to hint style */}
                      <div className="absolute inset-1 rounded pointer-events-none" style={{ border: `2px ${f.style === 'dashed' ? 'dashed' : f.style === 'dots' ? 'dotted' : 'solid'} ${f.primary}`, borderRadius: f.style === 'rounded' ? '10px' : '4px' }}></div>
                      <div className="relative z-10 text-center leading-tight">{f.label}</div>
                    </button>
                  ))}
                </div>
                <div className="text-[9px] opacity-60 mt-1.5">💡 Click any frame — preview updates instantly (right side)</div>
              </div>

              {/* Step 4 · Extra Contact Info */}
              <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/5 p-4" data-testid="brandmark-extras-panel">
                <div className="text-xs uppercase tracking-widest text-emerald-300 font-black mb-2 flex items-center gap-1">📞 Step 4 · Extra Contacts <span className="text-[9px] opacity-70">(visible on image + share text)</span></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  <input value={extras.url1} onChange={(e) => updExt('url1', e.target.value)} data-testid="brandmark-extra-url1" placeholder="🔗 URL 1 (website / catalog)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                  <input value={extras.mob1} onChange={(e) => updExt('mob1', e.target.value)} data-testid="brandmark-extra-mob1" placeholder="📞 Mobile 1" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                  <input value={extras.url2} onChange={(e) => updExt('url2', e.target.value)} data-testid="brandmark-extra-url2" placeholder="🔗 URL 2 (Insta / FB)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                  <input value={extras.mob2} onChange={(e) => updExt('mob2', e.target.value)} data-testid="brandmark-extra-mob2" placeholder="📞 Mobile 2" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                  <input value={extras.url3} onChange={(e) => updExt('url3', e.target.value)} data-testid="brandmark-extra-url3" placeholder="🔗 URL 3 (map / booking)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                  <input value={extras.mob3} onChange={(e) => updExt('mob3', e.target.value)} data-testid="brandmark-extra-mob3" placeholder="📞 Mobile 3" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                </div>
              </div>

              {/* Step 5 · Stickers (emoji overlays) */}
              <div className="rounded-2xl border border-pink-400/40 bg-pink-500/5 p-4" data-testid="brandmark-stickers-panel">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                  <div className="text-xs uppercase tracking-widest text-pink-300 font-black flex items-center gap-1">✨ Step 5 · Stickers <span className="text-[9px] opacity-70">({stickers.length}/6)</span></div>
                  <button onClick={() => setStickers([])} data-testid="brandmark-stickers-clear" className="text-[10px] px-2 py-0.5 rounded bg-white/10 opacity-70 hover:opacity-100">Clear</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {STICKER_OPTIONS.map((emoji, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        if (stickers.length >= 6) { toast.error('Max 6 stickers'); return; }
                        const x = 150 + Math.random() * (BASE_W - 300);
                        const y = 150 + Math.random() * (BASE_H - 500);
                        setStickers((prev) => [...prev, { emoji, x, y }]);
                      }}
                      data-testid={`brandmark-sticker-${i}`}
                      className="text-2xl w-11 h-11 rounded-lg bg-white/5 hover:bg-white/15 hover:scale-110 transition-all"
                      title={`Add ${emoji}`}>{emoji}</button>
                  ))}
                </div>
                <div className="text-[9px] opacity-60 mt-1.5">💡 Click any sticker to add to photo (random position). Max 6.</div>
              </div>

              {/* Step 6 · Blinking Text (multi-font · glow · animated) */}
              <div className="rounded-2xl border border-cyan-400/40 bg-cyan-500/5 p-4" data-testid="brandmark-blink-panel">
                <div className="text-xs uppercase tracking-widest text-cyan-300 font-black mb-2 flex items-center gap-1">💡 Step 6 · Blinking Text <span className="text-[9px] opacity-70">(8 fonts · animated video export)</span></div>
                <input value={blinkText} onChange={(e) => setBlinkText(e.target.value)} data-testid="brandmark-blink-text" placeholder="Aapka attractive message… e.g. GRAND SALE 50% OFF" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-cyan-400/30 text-xs mb-2" maxLength={60} />
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <select value={blinkFont} onChange={(e) => setBlinkFont(e.target.value)} data-testid="brandmark-blink-font" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-[11px]">
                    {BLINK_FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                  <select value={blinkStyle} onChange={(e) => setBlinkStyle(e.target.value)} data-testid="brandmark-blink-style" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-[11px]">
                    {BLINK_STYLES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
                <button onClick={recordVideo} disabled={recording || !blinkText.trim()} data-testid="brandmark-record-video" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-500 via-fuchsia-500 to-pink-500 text-white text-xs font-black disabled:opacity-40 flex items-center justify-center gap-1">
                  {recording ? <><Loader2 className="w-3 h-3 animate-spin" /> Recording 3s…</> : <>🎬 Save Animated Video (WebM · 3s)</>}
                </button>
                <div className="text-[9px] opacity-60 mt-1.5">💡 Text blinks in preview. Static PNG shows text prominently. For REAL blink → save video + share as WhatsApp status.</div>
              </div>
            </div>

            {/* Right: Canvas preview */}
            <div className="space-y-3">
              <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
                <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-2 flex items-center justify-between">
                  <span>Live Preview {rendering && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}</span>
                  <button onClick={draw} className="text-[10px] flex items-center gap-1 opacity-70 hover:opacity-100"><RefreshCw className="w-3 h-3" /> Redraw</button>
                </div>
                <div className="w-full aspect-square rounded-lg overflow-hidden bg-slate-950 border border-slate-800">
                  <canvas ref={canvasRef} data-testid="brandmark-canvas" className="w-full h-full object-contain" />
                </div>
                {!photoImg && (
                  <div className="text-center text-xs text-slate-500 py-4">👆 Photo upload karo — live preview yahaan dikhega</div>
                )}
              </div>

              {/* Auto-rotating caption + Language switcher + Custom feedback */}
              <div className="rounded-2xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-3" data-testid="brandmark-caption-panel">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 font-black">Message · {useCustom ? 'Custom (yours)' : 'Viral (auto-rotate)'}</div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                      <input type="checkbox" checked={useCustom} onChange={(e) => { setUseCustom(e.target.checked); localStorage.setItem('brandmark:useCustom', e.target.checked ? '1' : '0'); }} data-testid="brandmark-custom-toggle" />
                      Custom
                    </label>
                    <Globe className="w-3 h-3 text-fuchsia-300" />
                    <select
                      value={captionLang}
                      onChange={(e) => { setCaptionLang(e.target.value); localStorage.setItem('brandmark:lang', e.target.value); setCaptionIdx(0); }}
                      data-testid="brandmark-lang-select"
                      disabled={useCustom}
                      className="bg-slate-800 text-white text-[11px] rounded-lg px-2 py-1 border border-fuchsia-400/40 focus:outline-none disabled:opacity-50"
                    >
                      {Object.entries(languageLabels).map(([code, label]) => (
                        <option key={code} value={code}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {useCustom ? (
                  <textarea
                    value={customFeedback}
                    onChange={(e) => { setCustomFeedback(e.target.value); localStorage.setItem('brandmark:customFeedback', e.target.value); }}
                    placeholder="Apna feedback / message yahaan likho — koi bhi language, unlimited creativity 💫"
                    rows={4}
                    data-testid="brandmark-custom-feedback"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-fuchsia-400/30 text-sm min-h-[3.5em]"
                  />
                ) : (
                  <div className="text-sm min-h-[3.5em]" data-testid="brandmark-caption-text">{captions[captionIdx] || '—'}</div>
                )}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {!useCustom && <button onClick={() => setCaptionIdx((i) => (i + 1) % Math.max(1, captions.length))} className="px-2 py-1 rounded-lg bg-white/10 text-[11px] flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Next</button>}
                  <button onClick={copyCaption} data-testid="brandmark-copy-caption" className="px-2 py-1 rounded-lg bg-white/10 text-[11px] flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                </div>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={download} data-testid="brandmark-download" disabled={!photoImg} className="px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-black text-sm flex items-center justify-center gap-1 disabled:opacity-40">
                  <Download className="w-4 h-4" /> Download JPG
                </button>
                <button onClick={shareWhatsApp} data-testid="brandmark-share-whatsapp" className="px-4 py-3 rounded-xl bg-emerald-500 text-black font-black text-sm flex items-center justify-center gap-1">
                  <Sparkles className="w-4 h-4" /> Share on WhatsApp
                </button>
              </div>
              <div className="text-[10px] text-slate-500 text-center">Tip: Download karo phir WhatsApp/Instagram me photo attach karke caption paste karo — max reach 🚀</div>
            </div>
          </div>
        )}

        {/* Footer info */}
        <div className="mt-8 text-center text-[10px] text-slate-500">
          Anant BrandMark · 100% offline canvas · Zero server cost · Powered by Anant Web Portal
        </div>
      </div>
    </div>
  );
}

const PLANS_LABELS = { monthly: 'Monthly (300 AT)', yearly: 'Yearly (3000 AT)' };
